import React from "react";
import { formatTicketNote } from "../lib/formatTicketNote.js";

/**
 * Renders a ConnectWise email note readably.
 *
 * CW notes arrive as raw email bodies: quoted reply chains, signatures,
 * `cid:` inline images and Proofpoint/Outlook safelinks. `formatTicketNote`
 * cleans them; the reviewer can still reveal the untouched email.
 */
export function NoteContent({ content }: { content: string }) {
  const [showOriginal, setShowOriginal] = React.useState(false);
  const formatted = React.useMemo(() => formatTicketNote(content), [content]);
  const trimmed = formatted.trimmedQuoted || formatted.trimmedSignature;
  const html = showOriginal ? formatted.originalHtml : formatted.html;

  return (
    <div>
      <div
        className={
          "max-w-full overflow-hidden break-words text-sm text-[hsl(var(--content))] leading-relaxed " +
          "[&_a]:text-brand-600 [&_a]:underline " +
          "[&_table]:w-full [&_table]:text-left [&_table]:my-2 [&_table]:border-collapse " +
          "[&_th]:border [&_th]:border-[hsl(var(--border))] [&_th]:px-2 [&_th]:py-1 [&_th]:font-semibold [&_th]:bg-[hsl(var(--surface-subtle))] " +
          "[&_td]:border [&_td]:border-[hsl(var(--border))] [&_td]:px-2 [&_td]:py-1 " +
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 " +
          "[&_p]:mb-2 [&_p]:last:mb-0 " +
          "[&_pre]:bg-[hsl(var(--surface-subtle))] [&_pre]:rounded-lg [&_pre]:p-2 [&_pre]:overflow-x-auto " +
          "[&_blockquote]:border-l-2 [&_blockquote]:border-[hsl(var(--border))] [&_blockquote]:pl-3 [&_blockquote]:text-[hsl(var(--content-subtle))]"
        }
        style={{ overflowWrap: "anywhere" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {trimmed && (
        <button
          type="button"
          onClick={() => setShowOriginal((v) => !v)}
          className="mt-2 text-xs text-[hsl(var(--content-subtle))] underline hover:text-[hsl(var(--content))]"
        >
          {showOriginal
            ? "Hide quoted reply and signature"
            : `Show full email${formatted.trimmedQuoted ? " (quoted reply)" : ""}${
                formatted.trimmedSignature ? " (signature)" : ""
              }`}
        </button>
      )}
    </div>
  );
}
